/**
 * Reelia Logo Mark Concepts
 * Three geometric SVG icon marks — all work at any size from 16px to 512px.
 *
 * Usage:
 *   import { LogoA, LogoB, LogoC } from '../components/brand/LogoMarks';
 *   <LogoA size={32} />
 */

/* ─── Logo A — Signal Hexagon ────────────────────────────────────────────── */
/**
 * Filled hexagon + white signal arcs rising from a center dot.
 * Represents: AI signal detection, strength, precision.
 * Primary mark — works best as favicon, navbar, app icon.
 */
export function LogoA({ size = 100, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Reelia logo — Signal Hexagon"
    >
      <defs>
        <linearGradient id="a-fill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3B8AFF" />
          <stop offset="100%" stopColor="#1554C4" />
        </linearGradient>
      </defs>

      {/* Hexagon body */}
      <polygon
        points="50,4 89,27 89,73 50,96 11,73 11,27"
        fill="url(#a-fill)"
      />

      {/* Signal arc 3 — outermost */}
      <path
        d="M 23,74 A 27,27 0 0 1 77,74"
        fill="none"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
        opacity="0.38"
      />

      {/* Signal arc 2 — middle */}
      <path
        d="M 31,74 A 19,19 0 0 1 69,74"
        fill="none"
        stroke="white"
        strokeWidth="2.8"
        strokeLinecap="round"
        opacity="0.65"
      />

      {/* Signal arc 1 — innermost */}
      <path
        d="M 40,74 A 10,10 0 0 1 60,74"
        fill="none"
        stroke="white"
        strokeWidth="3.5"
        strokeLinecap="round"
        opacity="0.95"
      />

      {/* Center dot */}
      <circle cx="50" cy="74" r="4.5" fill="white" />
    </svg>
  );
}

/* ─── Logo B — Radar Node ─────────────────────────────────────────────────── */
/**
 * Dark circle with radar scan sweep, detection node, bright core.
 * Represents: AI detection, precision scanning, real-time analysis.
 * Works well as outlined dark icon on light or dark backgrounds.
 */
export function LogoB({ size = 100, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Reelia logo — Radar Node"
    >
      <defs>
        <linearGradient id="b-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0E1826" />
          <stop offset="100%" stopColor="#090D18" />
        </linearGradient>
        <radialGradient id="b-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#2E79FF" stopOpacity="1" />
          <stop offset="100%" stopColor="#1554C4" stopOpacity="1" />
        </radialGradient>
      </defs>

      {/* Background circle */}
      <circle cx="50" cy="50" r="47" fill="url(#b-bg)" />
      <circle cx="50" cy="50" r="47" fill="none" stroke="#2E79FF" strokeWidth="1.5" />

      {/* Outer ring */}
      <circle cx="50" cy="50" r="37" fill="none" stroke="rgba(46,121,255,0.18)" strokeWidth="1" />

      {/* Inner dashed ring */}
      <circle
        cx="50" cy="50" r="23"
        fill="none"
        stroke="rgba(46,121,255,0.28)"
        strokeWidth="1"
        strokeDasharray="4.5 3.5"
      />

      {/* Scan sweep sector */}
      <path
        d="M 50,50 L 50,13 A 37,37 0 0 1 84,69 Z"
        fill="rgba(46,121,255,0.14)"
      />

      {/* Scan line */}
      <line
        x1="50" y1="50" x2="50" y2="13"
        stroke="#2E79FF"
        strokeWidth="1.8"
        strokeLinecap="round"
      />

      {/* Crosshair ticks — 4 directions */}
      <line x1="50" y1="5"  x2="50" y2="11"  stroke="rgba(46,121,255,0.5)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="50" y1="89" x2="50" y2="95"  stroke="rgba(46,121,255,0.3)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="5"  y1="50" x2="11" y2="50"  stroke="rgba(46,121,255,0.3)" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="89" y1="50" x2="95" y2="50"  stroke="rgba(46,121,255,0.5)" strokeWidth="1.5" strokeLinecap="round" />

      {/* Detected node — upper right */}
      <circle cx="73" cy="24" r="4.5" fill="#1ECDE3" />
      <circle cx="73" cy="24" r="8.5" fill="none" stroke="#1ECDE3" strokeWidth="1" opacity="0.38" />

      {/* Core */}
      <circle cx="50" cy="50" r="6.5" fill="url(#b-core)" />
      <circle cx="50" cy="50" r="3" fill="white" opacity="0.9" />
    </svg>
  );
}

/* ─── Logo C — Content Pipeline ──────────────────────────────────────────── */
/**
 * Hexagon outline with 3 colored nodes (blue, teal, purple) connected to center.
 * Represents: the 3-stage content pipeline — Analyse, Create, Publish.
 * The most "brand system" oriented mark — potential evolving logo symbol.
 */
export function LogoC({ size = 100, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Reelia logo — Content Pipeline"
    >
      {/* Hexagon outline */}
      <polygon
        points="50,4 89,27 89,73 50,96 11,73 11,27"
        fill="none"
        stroke="rgba(46,121,255,0.32)"
        strokeWidth="2"
        strokeLinejoin="round"
      />

      {/* Connection lines: center → nodes */}
      <line x1="50" y1="50" x2="50"  y2="18"  stroke="rgba(46,121,255,0.35)"  strokeWidth="1.5" />
      <line x1="50" y1="50" x2="76"  y2="64"  stroke="rgba(30,205,227,0.35)"  strokeWidth="1.5" />
      <line x1="50" y1="50" x2="24"  y2="64"  stroke="rgba(139,92,246,0.35)"  strokeWidth="1.5" />

      {/* Node 1 — top (Analyse) — Electric Blue */}
      <circle cx="50" cy="18" r="7.5" fill="#2E79FF" />
      <circle cx="50" cy="18" r="7.5" fill="none" stroke="rgba(46,121,255,0.4)" strokeWidth="3" opacity="0.35" />

      {/* Node 2 — bottom right (Create) — Teal */}
      <circle cx="76" cy="64" r="7.5" fill="#1ECDE3" />
      <circle cx="76" cy="64" r="7.5" fill="none" stroke="rgba(30,205,227,0.4)" strokeWidth="3" opacity="0.35" />

      {/* Node 3 — bottom left (Publish) — Purple */}
      <circle cx="24" cy="64" r="7.5" fill="#8B5CF6" />
      <circle cx="24" cy="64" r="7.5" fill="none" stroke="rgba(139,92,246,0.4)" strokeWidth="3" opacity="0.35" />

      {/* Center hub */}
      <circle cx="50" cy="50" r="5.5" fill="white" opacity="0.12" />
      <circle cx="50" cy="50" r="3.5" fill="white" opacity="0.75" />
    </svg>
  );
}
